/**
 * Secure file intake.
 *
 * Detects document type from *magic bytes* (not just extension), enforces size
 * limits, and produces an immutable reference to the original bytes plus a
 * session-scoped id that is unrelated to the filename.
 *
 * Engineering rule: keep original files unchanged; export to a new file.
 */

import type { DocumentKind } from "./types";

/** 100 MB default ceiling for a single file in the local-only MVP. */
export const DEFAULT_MAX_BYTES = 100 * 1024 * 1024;

export type IntakeErrorCode =
  | "empty"
  | "too-large"
  | "unknown-type"
  | "extension-mismatch";

export class IntakeError extends Error {
  readonly code: IntakeErrorCode;
  constructor(code: IntakeErrorCode, message: string) {
    super(message);
    this.name = "IntakeError";
    this.code = code;
  }
}

export interface OriginalDocument {
  /** Session id, unrelated to the filename. */
  id: string;
  kind: DocumentKind;
  displayName: string;
  byteLength: number;
  /** Immutable copy of the original bytes. Do not mutate. */
  readonly bytes: Uint8Array;
}

const PDF_MAGIC = [0x25, 0x50, 0x44, 0x46] as const; // "%PDF"
const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04] as const; // "PK\x03\x04"
const ZIP_EMPTY = [0x50, 0x4b, 0x05, 0x06] as const; // empty archive
const ZIP_SPANNED = [0x50, 0x4b, 0x07, 0x08] as const;

/** ODF text mimetype stored as the first ZIP part in every `.odt`. */
export const ODT_MIMETYPE = "application/vnd.oasis.opendocument.text";

function startsWith(bytes: Uint8Array, sig: readonly number[]): boolean {
  if (bytes.length < sig.length) return false;
  for (let i = 0; i < sig.length; i++) {
    if (bytes[i] !== sig[i]) return false;
  }
  return true;
}

function isZip(bytes: Uint8Array): boolean {
  return (
    startsWith(bytes, ZIP_MAGIC) ||
    startsWith(bytes, ZIP_EMPTY) ||
    startsWith(bytes, ZIP_SPANNED)
  );
}

/** Compare an ASCII string against bytes starting at `offset`. */
function asciiAt(bytes: Uint8Array, offset: number, text: string): boolean {
  if (bytes.length < offset + text.length) return false;
  for (let i = 0; i < text.length; i++) {
    if (bytes[offset + i] !== text.charCodeAt(i)) return false;
  }
  return true;
}

/**
 * Content-true ODT detection.
 *
 * Every conforming ODF package stores an uncompressed `mimetype` part as the
 * first ZIP entry. That fixes the local-file-header layout: the filename
 * "mimetype" begins at byte 30 and its (stored) data begins at byte 38. So we
 * can confirm an `.odt` from the bytes alone, without unzipping and without
 * trusting the extension.
 */
export function looksLikeOdt(bytes: Uint8Array): boolean {
  return (
    startsWith(bytes, ZIP_MAGIC) &&
    asciiAt(bytes, 30, "mimetype") &&
    asciiAt(bytes, 38, ODT_MIMETYPE)
  );
}

/**
 * Detect the document kind from magic bytes.
 *
 * DOCX and ODT are both ZIP containers. ODT is identified from its embedded
 * `mimetype` part (content-true). DOCX is an OOXML ZIP whose `word/` part is
 * confirmed later during inspection; the `.docx` extension disambiguates it
 * from other OOXML/plain ZIPs here.
 */
export function detectKind(
  bytes: Uint8Array,
  fileName: string,
): DocumentKind | null {
  if (startsWith(bytes, PDF_MAGIC)) return "pdf";
  if (isZip(bytes)) {
    // ODF text: trust the embedded mimetype, then fall back to the extension
    // for edge cases where the mimetype part is compressed or reordered.
    if (looksLikeOdt(bytes) || /\.odt$/i.test(fileName)) return "odt";
    // OOXML DOCX vs other zip: extension is the practical disambiguator here;
    // deeper confirmation happens in the DOCX ZIP inspection step.
    if (/\.docx$/i.test(fileName)) return "docx";
    // A ZIP without a recognised name is ambiguous; treat as unknown for safety.
    return null;
  }
  return null;
}

function extensionMatches(kind: DocumentKind, fileName: string): boolean {
  if (kind === "pdf") return /\.pdf$/i.test(fileName);
  if (kind === "odt") return /\.odt$/i.test(fileName);
  return /\.docx$/i.test(fileName);
}

function generateId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
}

export interface IntakeOptions {
  maxBytes?: number;
}

/**
 * Validate raw bytes + filename and produce an immutable OriginalDocument.
 * Pure and synchronous so it is trivially unit-testable and can run in a worker.
 */
export function intakeBytes(
  bytes: Uint8Array,
  fileName: string,
  options: IntakeOptions = {},
): OriginalDocument {
  const maxBytes = options.maxBytes ?? DEFAULT_MAX_BYTES;

  if (bytes.length === 0) {
    throw new IntakeError("empty", "The file is empty.");
  }
  if (bytes.length > maxBytes) {
    throw new IntakeError(
      "too-large",
      `The file is larger than the ${Math.round(maxBytes / (1024 * 1024))} MB limit.`,
    );
  }

  const kind = detectKind(bytes, fileName);
  if (!kind) {
    throw new IntakeError(
      "unknown-type",
      "This file is not a supported PDF, Word (.docx) or OpenDocument (.odt) document.",
    );
  }
  if (!extensionMatches(kind, fileName)) {
    throw new IntakeError(
      "extension-mismatch",
      `The file contents look like a ${kind.toUpperCase()} but the name does not match.`,
    );
  }

  // Take an immutable copy so later page/annotation edits never touch the source.
  const copy = bytes.slice();

  return {
    id: generateId(),
    kind,
    displayName: fileName,
    byteLength: copy.length,
    bytes: copy,
  };
}

/** Read a File/Blob into an OriginalDocument. */
export async function intakeFile(
  file: File,
  options: IntakeOptions = {},
): Promise<OriginalDocument> {
  const buffer = await file.arrayBuffer();
  return intakeBytes(new Uint8Array(buffer), file.name, options);
}
